import {
  addTime,
  adjustFromTimeZone,
  adjustToTimeZone,
  containsValue,
  getDefaultTimeFrame,
  getTimeFrameDates,
  getTimeStatisticsRecord,
  transportMethod,
  TransportMethod,
  isBefore,
  startOf,
  StatisticsGroupWrapper,
  statisticsType,
  TimeStatisticsWrapper,
  TransportCategory,
  VolumeInventorySnapshotEntity,
  VolumeInventorySnapshotEntryEntity,
  volumeInventoryVolumeType,
  VolumeInventoryVolumeType
} from "@ots-frontend/ots-data";
import {useMemo} from "react";
import {getDateFormatterOptions,} from "../../chart/TimeChart/TimeChart";
import {useTranslation} from "react-i18next";
import {LineChart, LineChartBucketData, LineChartData, LineChartProps} from "../../chart/LineChart/LineChart";
import {Point} from "chart.js";

export type VolumeInventoryByTimeChartProps = Pick<LineChartProps, 'id'> & {
  fromDttm: number
  toDttm?: number
  snapshots?: VolumeInventorySnapshotEntity[]
  hauledStats?: StatisticsGroupWrapper | StatisticsGroupWrapper[]
  types?: VolumeInventoryVolumeType[],
  splitTypes?: boolean
  methods?: TransportMethod[] | undefined,
  splitMethods?: boolean
  category?: TransportCategory
}

const getVolume = (types: VolumeInventoryVolumeType[], methods: TransportMethod[], entry: VolumeInventorySnapshotEntryEntity): number => {
  let volume = 0

  if (containsValue(types, volumeInventoryVolumeType.usable)) {
    if (containsValue(methods, transportMethod.trucking)) {
      volume += entry.volume;
    }
    if (containsValue(methods, transportMethod.pipeline)) {
      volume += entry.pipelineVolume;
    }
  }

  if (containsValue(types, volumeInventoryVolumeType.unusable)) {
    if (containsValue(methods, transportMethod.trucking)) {
      volume += entry.unusableVolume;
    }
    if (containsValue(methods, transportMethod.pipeline)) {
      volume += entry.pipelineUnusableVolume;
    }
  }

  if (containsValue(types, volumeInventoryVolumeType.secondaryUsable)) {
    if (containsValue(methods, transportMethod.trucking)) {
      volume += entry.secondaryVolume;
    }
    if (containsValue(methods, transportMethod.pipeline)) {
      volume += entry.pipelineSecondaryVolume;
    }
  }

  if (containsValue(types, volumeInventoryVolumeType.secondaryUnusable)) {
    if (containsValue(methods, transportMethod.trucking)) {
      volume += entry.secondaryUnusableVolume;
    }
    if (containsValue(methods, transportMethod.pipeline)) {
      volume += entry.pipelineSecondaryUnusableVolume;
    }
  }

  return volume
}

const getHauledVolume = (methods: TransportMethod[], category: TransportCategory | undefined, entry: TimeStatisticsWrapper | undefined): number | Point | null => {
  let volume : number | null = null

  if (entry) {
    for (const method of methods) {
      if (typeof volume === 'undefined') {
        volume = 0
      }
      if (volume !== null) {
        volume += entry.getValue(method, statisticsType.quantity, undefined, category)
      }
    }
  }

  return volume
}

export const VolumeInventoryByTimeChart = (props: VolumeInventoryByTimeChartProps) => {
  const {
    id,
    fromDttm,
    toDttm = new Date().getTime(),
    snapshots,
    hauledStats,
    types = [volumeInventoryVolumeType.usable],
    splitTypes = false,
    methods = [transportMethod.trucking, transportMethod.pipeline],
    splitMethods = true,
    category,
  } = props

  const {t} = useTranslation()

  const timeFrame = useMemo(() => {
    const defaultTimeFrame = getDefaultTimeFrame(fromDttm, toDttm)
    if (defaultTimeFrame === 'MONTH' || defaultTimeFrame === 'WEEK') {
      return "DAY"
    }
    return defaultTimeFrame
  }, [fromDttm, toDttm])

  const chartData: LineChartData = useMemo(() => {
    if (snapshots) {
      const dates: string[] = []
      const buckets: Record<string, LineChartBucketData> = {}

      const timeFrameDates = getTimeFrameDates(timeFrame, fromDttm, toDttm)

      let startDttm = timeFrameDates.startDttm
      const endDttm = timeFrameDates.endDttm

      const formatter = new Intl.DateTimeFormat(undefined, getDateFormatterOptions(timeFrame))

      while (isBefore(startDttm, endDttm)) {
        dates.push(formatter.format(startDttm))

        snapshots.forEach((snapshot) => {
          if (!category || category.cargoSubtype === snapshot.cargoSubtype) {
            const objectName = snapshot.objectName ?? "Unknown"

            const entry = snapshot.entries.find((entry) => startOf(timeFrame, adjustFromTimeZone(entry.dttm)).getTime() === startDttm.getTime())

            if (splitTypes && splitMethods) {
              for (const type of types) {
                for (const method of methods) {
                  let volume: number | Point | null = null
                  if (entry) {
                    volume = getVolume([type], [method], entry)
                  }
                  const bucketName = objectName + " - " + t(method.displayValue.id, method.displayValue.defaultMessage) + " " + t(type.displayValue.id, type.displayValue.defaultMessage)
                  if (bucketName in buckets) {
                    buckets[bucketName].values.push(volume)
                  } else {
                    buckets[bucketName] = {
                      values: [volume]
                    }
                  }
                }
              }
            } else if (splitTypes) {
              for (const type of types) {
                let volume: number | Point | null = null
                if (entry) {
                  volume = getVolume([type], methods, entry)
                }

                const bucketName = objectName + " - " + t(type.displayValue.id, type.displayValue.defaultMessage)
                if (bucketName in buckets) {
                  buckets[bucketName].values.push(volume)
                } else {
                  buckets[bucketName] = {
                    values: [volume]
                  }
                }
              }
            } else if (splitMethods) {
              for (const method of methods) {
                let volume: number | Point | null = null
                if (entry) {
                  volume = getVolume(types, [method], entry)
                }
                const bucketName = objectName + " - " + t(method.displayValue.id, method.displayValue.defaultMessage)
                if (bucketName in buckets) {
                  buckets[bucketName].values.push(volume)
                } else {
                  buckets[bucketName] = {
                    values: [volume]
                  }
                }
              }
            } else {
              let volume: number | Point | null = null
              if (entry) {
                volume = getVolume(types, methods, entry);
              }

              if (objectName in buckets) {
                buckets[objectName].values.push(volume)
              } else {
                buckets[objectName] = {
                  values: [volume]
                }
              }
            }
          }
        })

        const stats: StatisticsGroupWrapper[] = []
        if (hauledStats) {
          if (Array.isArray(hauledStats)) {
            stats.push(...hauledStats)
          } else {
            stats.push(hauledStats)
          }
        }

        stats.forEach((stat) => {
          const timeStatsRecord = getTimeStatisticsRecord(timeFrame, stat)
          const timeStats = timeStatsRecord[adjustToTimeZone(startDttm).getTime()]

          const objectName = "(Hauled) " + (stat.name ?? "Unknown")

          if (splitMethods) {
            for (const method of methods) {
              const volume: number | Point | null = getHauledVolume([method], category, timeStats)
              const bucketName = objectName + " - " + t(method.displayValue.id, method.displayValue.defaultMessage)
              if (bucketName in buckets) {
                buckets[bucketName].values.push(volume)
              } else {
                buckets[bucketName] = {
                  stack: 'haul',
                  order: -1,
                  borderDash: [5, 5],
                  values: [volume]
                }
              }
            }
          } else {
            const volume: number | Point | null = getHauledVolume(methods, category, timeStats)

            if (objectName in buckets) {
              buckets[objectName].values.push(volume)
            } else {
              buckets[objectName] = {
                stack: 'haul',
                order: -1,
                borderDash: [5, 5],
                values: [volume]
              }
            }
          }
        })

        startDttm = addTime(timeFrame, startDttm, 1)
      }

      return {
        titles: dates,
        buckets: buckets,
      }
    }

    return {
      titles: [],
      buckets: {}
    }
  }, [snapshots, hauledStats, timeFrame, fromDttm, toDttm, category, splitTypes, splitMethods, types, methods, t])

  return (
    <LineChart
      id={id}
      stacked={{
        enabled: true,
        filled: true,
      }}
      xAxisTitle={t<string>("common.insight.volumeinventorybytime.xaxis.title", "Time")}
      yAxisTitle={t<string>("common.insight.volumeinventorybytime.yaxis.title", "Inventory")}
      data={chartData}/>
  )
}
